import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getStickyNotes } from '../Api'
import { getSocketClient } from '../Helper/socketClient'

// Helper: returns { call, flush, cancel }
function makeCombinedSender(fn, throttleMs = 120, debounceMs = 300) {
    let lastRun = 0;
    let debTimer = null;
    let lastPayload = null;

    const throttled = (payload) => {
        const now = Date.now();
        if (now - lastRun >= throttleMs) {
            lastRun = now;
            try { fn(payload); } catch (e) { console.error(e); }
        }
    };

    const scheduleDebounce = (payload) => {
        lastPayload = payload;
        if (debTimer) clearTimeout(debTimer);
        debTimer = setTimeout(() => {
            try {
                if (lastPayload !== null) {
                    fn(lastPayload);
                }
            } catch (e) { console.error(e); }
            debTimer = null;
            lastPayload = null;
        }, debounceMs);
    };

    return {
        call(payload) {
            throttled(payload);
            scheduleDebounce(payload);
        },
        flush() {
            if (debTimer) {
                clearTimeout(debTimer);
                debTimer = null;
            }
            if (lastPayload !== null) {
                try { fn(lastPayload); } catch (e) { console.error(e); }
                lastPayload = null;
            }
        },
        cancel() {
            if (debTimer) {
                clearTimeout(debTimer);
                debTimer = null;
            }
            lastPayload = null;
        }
    };
}

// localStorage helpers for remembering isOpen state per quote
const getLocalIsOpen = (quoteId) => {
    try {
        const val = localStorage.getItem(`stickyNote-${quoteId}-isOpen`);
        return val !== null ? val === 'true' : null;
    } catch { return null; }
};

const setLocalIsOpen = (quoteId, isOpen) => {
    try {
        localStorage.setItem(`stickyNote-${quoteId}-isOpen`, String(isOpen));
    } catch { /* ignore */ }
};

export default function StickyNote({ quoteId }) {
    const loginToken = useSelector((state) => state.reducer.userToken)
    const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn)

    const localIsOpen = getLocalIsOpen(quoteId);

    const [state, setState] = useState({
        isOpen: localIsOpen !== null ? localIsOpen : false,
        content: ''
    })
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [hasError, setHasError] = useState(false)

    const isInitialLoadRef = useRef(true)
    const textareaRef = useRef(null)
    const panelRef = useRef(null)
    const socketRef = useRef(null)

    // Fetch initial data
    useEffect(() => {
        if (!quoteId || !loginToken || !userLoggedIn?.role) {
            setIsLoading(false)
            return
        }

        const fetchStickyNote = async () => {
            try {
                setIsLoading(true)
                setHasError(false)
                const response = await getStickyNotes({
                    token: loginToken,
                    role: userLoggedIn.role,
                    id: quoteId
                })

                if (response.status === 200 && response.stickyNote) {
                    const note = response.stickyNote
                    const localPref = getLocalIsOpen(quoteId);
                    setState({
                        isOpen: localPref !== null ? localPref : (note.isOpen !== undefined ? note.isOpen : false),
                        content: note.content || ''
                    })
                }
            } catch (error) {
                console.error('Error fetching sticky note:', error)
                if (error.response?.status !== 404) {
                    setHasError(true)
                }
            } finally {
                setIsLoading(false)
                isInitialLoadRef.current = false
            }
        }

        fetchStickyNote()
    }, [quoteId, loginToken, userLoggedIn?.role])

    // Socket connection
    useEffect(() => {
        if (!quoteId || !loginToken || !userLoggedIn?.role) return

        const socket = getSocketClient(loginToken)
        if (!socket) return

        socketRef.current = socket
        socket.emit('sticky-note:join-quote', { quoteId })

        socket.on('sticky-note:joined', ({ quoteId: joinedQuoteId }) => {
            console.log(`Joined sticky note room for quote ${joinedQuoteId}`)
        })

        socket.on('sticky-note:updated', ({ stickyNote, isBuffered }) => {
            if (stickyNote.quoteId?.toString() !== quoteId?.toString()) return

            setState(prev => ({
                ...prev,
                content: stickyNote.content !== undefined ? stickyNote.content : prev.content,
            }))

            if (!isBuffered) {
                setIsSaving(false)
                setHasError(false)
            }
        })

        socket.on('sticky-note:error', ({ message, error }) => {
            console.error('Sticky note error:', message, error)
            setHasError(true)
            setIsSaving(false)
        })

        return () => {
            if (socket) {
                socket.off('sticky-note:joined')
                socket.off('sticky-note:updated')
                socket.off('sticky-note:error')
            }
        }
    }, [quoteId, loginToken, userLoggedIn?.role])

    const sendUpdate = useCallback((updateData) => {
        if (!quoteId || !socketRef.current || isInitialLoadRef.current) return

        try {
            setIsSaving(true)
            setHasError(false)
            socketRef.current.emit('sticky-note:update', {
                quoteId,
                ...updateData
            })
        } catch (error) {
            console.error('Error sending sticky note update:', error)
            setHasError(true)
            setIsSaving(false)
        }
    }, [quoteId])

    // Content sender with throttle/debounce
    const contentSenderRef = useRef(null);

    useEffect(() => {
        if (!sendUpdate) return;
        contentSenderRef.current = makeCombinedSender(
            (payload) => sendUpdate(payload),
            1000, 1000
        );
        return () => {
            try { contentSenderRef.current?.flush(); } catch (e) { }
            contentSenderRef.current = null;
        };
    }, [sendUpdate]);

    const sendImmediateUpdate = useCallback((updateData) => {
        if (!quoteId || !socketRef.current || isInitialLoadRef.current) return
        try {
            setIsSaving(true)
            setHasError(false)
            socketRef.current.emit('sticky-note:update', { quoteId, ...updateData })
        } catch (error) {
            console.error('Error sending sticky note update:', error)
            setHasError(true)
            setIsSaving(false)
        }
    }, [quoteId])

    function toggleOpen() {
        setState(prev => {
            const newIsOpen = !prev.isOpen;
            setLocalIsOpen(quoteId, newIsOpen);
            sendImmediateUpdate({ isOpen: newIsOpen });
            return { ...prev, isOpen: newIsOpen };
        });
    }

    function handleContentChange(e) {
        const val = e.target.value;
        setState(prev => ({ ...prev, content: val }));
        contentSenderRef.current?.call({ content: val });
    }

    // Close panel when clicking outside
    useEffect(() => {
        if (!state.isOpen) return;
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                toggleOpen();
            }
        };
        // Delay listener to avoid closing on the same click that opened
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [state.isOpen]);

    // Focus textarea when expanded
    useEffect(() => {
        if (state.isOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [state.isOpen]);

    const hasContent = state.content && state.content !== 'Type your note here...' && state.content.trim().length > 0;

    if (isLoading) return null;

    return (
        <div className="fixed bottom-6 right-6 z-[1000]" ref={panelRef}>
            {/* Expanded panel */}
            <div
                className={`absolute bottom-16 right-0 w-[300px] transition-all duration-200 ease-out origin-bottom-right ${
                    state.isOpen
                        ? 'opacity-100 scale-100 translate-y-0'
                        : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
                }`}
            >
                <div
                    className="rounded-xl overflow-hidden shadow-elevated border border-yellow-300/80"
                    style={{ background: 'linear-gradient(180deg, #fff9d6, #fff7b8)' }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                            <span className="text-[12px] font-semibold text-amber-900">Note</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {isSaving && (
                                <div className="flex items-center gap-1">
                                    <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-amber-700"></div>
                                    <span className="text-[10px] text-amber-700">Saving</span>
                                </div>
                            )}
                            {hasError && !isSaving && (
                                <span className="text-[10px] text-red-600 font-medium">Failed</span>
                            )}
                            <button
                                onClick={toggleOpen}
                                className="w-5 h-5 flex items-center justify-center rounded hover:bg-amber-200/50 transition-colors"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Textarea */}
                    <div className="px-3 pb-3">
                        <textarea
                            ref={textareaRef}
                            value={state.content}
                            onChange={handleContentChange}
                            className="w-full h-[200px] bg-transparent resize-none outline-none text-[13px] leading-relaxed placeholder:text-amber-700/40"
                            style={{ color: '#1a1a1a', caretColor: '#92400e' }}
                            placeholder="Write something..."
                        />
                    </div>
                </div>
            </div>

            {/* Minimized pill button */}
            <button
                onClick={toggleOpen}
                className={`flex items-center gap-2 rounded-full border border-yellow-300/80 px-3.5 py-2 shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-95 ${
                    state.isOpen ? 'ring-2 ring-yellow-300/70' : ''
                }`}
                style={{ background: 'linear-gradient(135deg, #fff9d6, #fef3a0)' }}
            >
                <div className="relative">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    {hasContent && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500" />
                    )}
                </div>
                <span className="text-[12px] font-medium text-amber-900">Notes</span>
            </button>
        </div>
    )
}
